import React from 'react';
import {
  Box,
  Divider,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useTheme,
  VStack,
} from '@chakra-ui/react';
import { SystemTranslationKeys } from '@common/entities';

import { useSystemTranslation } from 'hooks';
import { AllChargeDetail } from 'types/shopping-cart';
import { translateChargeDetailText } from 'utils/charge-utils';

interface Props {
  label: string;
  children: React.ReactNode;
  charges: AllChargeDetail[];
}

const badgeStyle = {
  borderRadius: 'md',
  flexShrink: 0,
  fontSize: '11px',
  fontWeight: 'bold',
  lineHeight: '1.35',
  px: '10px',
  py: '6px',
} as const;

interface InfoPillProps {
  fieldLabel: string;
  value: string;
}

enum RequirementType {
  Mandatory = 'Mandatory',
  Optional = 'Optional',
  Required = 'Required',
}

function InfoPill({ fieldLabel, value }: InfoPillProps) {
  const theme = useTheme();

  const infoBadgeSize = {
    ...badgeStyle,
    fontSize: '10px',
    color: theme.colors.gray[800],
    bg: theme.colors.gray[100],
    borderWidth: '1px',
    borderColor: theme.colors.gray[200],
    overflowWrap: 'break-word',
  } as const;

  return (
    <Box {...infoBadgeSize}>
      <Text as="span" fontWeight="bold">
        {fieldLabel}
      </Text>{' '}
      {value}
    </Box>
  );
}

const FeeTransparencyPopover = ({ label, children, charges }: Props) => {
  const theme = useTheme();
  const { tSystem } = useSystemTranslation();

  const getBadgeColor = (requirementType: string) => {
    switch (requirementType) {
      case RequirementType.Mandatory:
        return {
          color: theme.colors.red[600],
          bg: theme.colors.red[100],
        };
      case RequirementType.Required:
        return {
          color: theme.colors.yellow[800],
          bg: theme.colors.yellow[100],
        };
      default:
        return {
          color: theme.colors.black.base,
          bg: 'transparent',
        };
    }
  };

  return (
    <Popover trigger="hover" placement="top">
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent
          bg="white"
          maxW="340px"
          borderRadius="lg"
          boxShadow="0 4px 14px rgba(0, 0, 0, 0.08)"
          overflow="hidden"
          borderWidth="1px"
          borderColor="gray.100"
        >
          <PopoverArrow bg="white" />
          <Box px={4} pt={4} pb={0}>
            <Text fontWeight="bold" fontSize="sm" color="gray.800" lineHeight="short">
              {label}
            </Text>
            <Divider mt={3} borderColor="gray.200" />
          </Box>
          <PopoverBody px={4} pt={3} pb={4}>
            {charges.map((charge, index) => {
              const {
                timing,
                frequency,
                method,
                description,
                // isOptional
              } = charge;
              const translatedTiming = translateChargeDetailText(tSystem, 'timing', timing);
              const translatedFrequency = translateChargeDetailText(
                tSystem,
                'frequency',
                frequency,
              );
              const translatedMethod = translateChargeDetailText(tSystem, 'method', method);
              const translatedRequirement = translateChargeDetailText(
                tSystem,
                'requirement',
                charge.requirementType,
              );
              return (
                <VStack spacing={2} align="stretch" key={charge.scheduledChargeId}>
                  {description && (
                    <Text fontSize="13px" color="gray.700" lineHeight="short">
                      {description}
                    </Text>
                  )}
                  <Flex wrap="wrap" gap={2} align="flex-start">
                    <Box {...badgeStyle} {...getBadgeColor(charge.requirementType)}>
                      {translatedRequirement}
                    </Box>

                    {translatedTiming && (
                      <InfoPill
                        fieldLabel={`${tSystem(SystemTranslationKeys.cart.timing)}:`}
                        value={translatedTiming}
                      />
                    )}
                    {translatedFrequency && (
                      <InfoPill
                        fieldLabel={`${tSystem(SystemTranslationKeys.cart.frequency)}:`}
                        value={translatedFrequency}
                      />
                    )}
                    {translatedMethod && (
                      <InfoPill
                        fieldLabel={`${tSystem(SystemTranslationKeys.cart.method)}:`}
                        value={translatedMethod}
                      />
                    )}
                  </Flex>
                  {index !== charges.length - 1 && <Divider my={2} borderColor="gray.300" />}
                </VStack>
              );
            })}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default FeeTransparencyPopover;
